import { useApolloClient } from '@apollo/client'
import { useEffect, useRef, useState } from 'react'

import { ELIGIBLE_TICKET_WATCHERS_QUERY } from '../../graphql/tickets'

/**
 * The server's cap, mirrored here only to chunk beneath it.
 *
 * Not a second implementation of the rule: nothing about who is eligible is
 * decided locally. ticket-service rejects an oversized batch with
 * InvalidArgument rather than truncating it — deliberately, so a short answer
 * can never be mistaken for "the rest are ineligible" — which means a caller
 * that ignores the cap gets a broken picker rather than a wrong one.
 */
const BATCH = 100

export interface EligibleWatchers {
  /** Ids the server confirmed may be added. Empty until the first answer. */
  eligible: Set<string>
  /** True while any chunk is still outstanding. */
  loading: boolean
  /** Set when the lookup failed — the caller decides what to show. */
  error: Error | null
}

/**
 * Resolves which of `candidateIds` may be added as watchers on `ticketId`.
 *
 * CHUNKED, because the directory query is unpaginated. IAM applies a limit only
 * when one is asked for (`pagination != nil && PageSize > 0`), and the panel
 * asks for none — so this receives the entire directory, which on any estate
 * past a hundred people exceeds the server's cap. Sending it whole would fail
 * the query outright.
 *
 * Imperative rather than a stack of useQuery calls: the number of chunks
 * depends on the directory size, and hooks cannot be called in a loop whose
 * length varies between renders.
 *
 * A stale answer is DISCARDED, not merged. The candidate set changes whenever
 * a watcher is added — the new watcher leaves the list — and a slow response
 * from the previous set would otherwise re-admit someone already watching.
 */
export function useEligibleWatchers(
  ticketId: string,
  candidateIds: string[]
): EligibleWatchers {
  const client = useApolloClient()
  const [state, setState] = useState<EligibleWatchers>({
    eligible: new Set(),
    loading: candidateIds.length > 0,
    error: null
  })

  // Joined, not the array itself: a new array identity on every render would
  // re-run this effect forever, and the identity changes on each parent render
  // because `options` is rebuilt inline.
  //
  // SORTED, because the question this key answers is "did the candidate SET
  // change", and an unsorted join answers "did the set or its order change".
  // The order comes from the Apollo-cached directory, which nothing here sorts
  // and nothing promises to keep stable across a refetch or a normalized-cache
  // write — so a reordering with identical membership would re-fire the whole
  // chunked fan-out for an answer already held. Found by perf-engineer.
  const key = [...candidateIds].sort().join(',')
  // Guards against a response from a superseded candidate set being applied.
  const generation = useRef(0)

  useEffect(() => {
    // Bumped BEFORE the early return, not after it.
    //
    // Bumping it only on the path that fires a request left the guard absent
    // on the two paths that do not: render with candidates, request in flight;
    // re-render with none, hit the early return, reset the state — and leave
    // the counter untouched. The in-flight answer then arrives, finds its
    // generation still current, and overwrites the reset. That is exactly the
    // stale application the counter exists to prevent, on the one transition
    // nobody thinks about. Found by code-reviewer during the pre-merge gate.
    const mine = ++generation.current

    const ids = key ? key.split(',') : []
    if (!ticketId || ids.length === 0) {
      setState({ eligible: new Set(), loading: false, error: null })
      return
    }

    setState((prev) => ({ ...prev, loading: true, error: null }))

    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += BATCH) {
      chunks.push(ids.slice(i, i + BATCH))
    }

    // Promise.all, so ONE failing chunk fails the whole answer.
    //
    // A partial merge would be defensible — the chunks that answered are
    // server-confirmed — but it produces a picker that is quietly missing
    // people, which is the failure this whole change exists to remove, just
    // pointing the other way. A directory large enough for several chunks is
    // correspondingly more exposed to one transient failure blanking the list;
    // that is the accepted cost of never offering a name the server has not
    // confirmed.
    Promise.all(
      chunks.map((chunk) =>
        client.query<{ eligibleTicketWatchers: string[] }>({
          query: ELIGIBLE_TICKET_WATCHERS_QUERY,
          variables: { ticketId, userIds: chunk },
          // Eligibility turns on IAM grants that can change outside this app,
          // and a cached answer would keep offering someone whose grant was
          // revoked. The cost is one round trip per picker open.
          fetchPolicy: 'network-only'
        })
      )
    )
      .then((results) => {
        if (mine !== generation.current) return
        const eligible = new Set<string>()
        results.forEach((r) =>
          (r.data?.eligibleTicketWatchers ?? []).forEach((id) =>
            eligible.add(id)
          )
        )
        setState({ eligible, loading: false, error: null })
      })
      .catch((e: Error) => {
        if (mine !== generation.current) return
        // The set stays EMPTY on failure, and the caller is told why. Falling
        // back to the unfiltered directory would restore exactly the defect
        // this exists to fix — offering people the server will refuse —
        // silently, and only when the lookup is broken.
        setState({ eligible: new Set(), loading: false, error: e })
      })
  }, [client, ticketId, key])

  return state
}
